// Alt Surface Builder (IM-2487) — compute engine, live parameters, metrics,
// the realised-vol overlay and the computed ATM term-structure walkthrough.
//
// The screen reproduces the backend generated-token ATM methodology client-side
// (authorised: the user varies the parameters live). Editing a parameter
// recomputes from the cached daily-close frame and the ETH/BTC reference
// surfaces — no refetch. The published token surface supplies the published ATM
// columns and the build-time subtitle (its build timestamp); the stdev-vs-EWMA
// overlay visualises the realised-vol inputs that feed the ratios; and the
// computed ATM term-structure panel makes the generation chain legible end to
// end — the realised-vol ratios/spreads and the per-pillar ETH ATMF ->
// scale/spread(T) -> token ATMF step.
//
// Out of scope this pass: SABR wings — every surface view here is ATM-focused.
import { useEffect, useMemo, useState } from 'react';
import { CircularProgress } from '@mui/material';
import moment from 'moment';

import TokenSelector from '../v2/token-selector';
import { classifyRegistryTokens } from '../v2/token-selector/registry-rows';
import { useAvailableTokens } from '../../providers/exotics-tokens/use-available-tokens';
import {
  altSurfaceTokenFromRegistry,
  useAltSurfaceInputs,
} from '../../providers/alt-surface/use-alt-surface-inputs';
import {
  altVolSeries,
  columnCloses,
  computeAltSurface,
  extractAsOf,
  extractPublishedAtm,
  formatExpiryIn,
  AltSurfaceComputation,
  AltSurfaceMode,
  ALT_SURFACE_DEFAULT_MODE,
  AltSurfaceFrequency,
  ALT_SURFACE_DEFAULT_FREQUENCY,
  AltSurfaceScaleCurve,
  ALT_SURFACE_DEFAULT_SCALE_CURVE,
  ALT_SURFACE_DEFAULT_SCALE_EXPONENT,
  PublishedAtm,
  PublishedAtmByExpiry,
  PUBLISHED_RR_FLY_KEYS,
  PUBLISHED_CP_KEYS,
} from '../../providers/alt-surface/alt-surface-compute';
import {
  ALT_SURFACE_DEFAULT_PARAMS,
  ALT_SURFACE_PARAM_RANGES,
  AltSurfaceParams,
  applyAltSurfaceParam,
} from '../../providers/alt-surface/alt-surface-constants';
import {
  DAILY_CLOSE_COLUMNS,
  DailyCloseRow,
} from '../../providers/alt-surface/alt-surface-daily-closes';
import { useThemeMode } from '../../providers/theme-provider';
import { getTokenValue } from '../../styles/tokens';
import { formatTokenPrice } from '../../utils/decimal-map';
import type { ResolvedThemeMode } from '../../theme/theme-store';

import AltSurfaceChart, { AltChartSeries } from './alt-surface-chart';

import './styles.scss';

// Live parameter controls, seeded from the methodology defaults (constants
// module, not a component literal). Each edits one field of AltSurfaceParams via
// a range slider. The live value chip reads 'Selected — <n> d'; the left-hand
// `label` already names the parameter, so no per-control short label is needed.
// Bounds and step come from ALT_SURFACE_PARAM_RANGES so they are defined once.
const PARAM_CONTROLS: { key: keyof AltSurfaceParams; label: string }[] = [
  { key: 'windowSize', label: 'Window — short (days)' },
  { key: 'hlShort', label: 'EWMA half-life — short (days)' },
  { key: 'windowLong', label: 'Window — long (days)' },
  { key: 'hlLong', label: 'EWMA half-life — long (days)' },
];

// Wire format of the published surface timestamp is either ISO-8601 (with a
// zone) or a naive "YYYY-MM-DD HH:MM:SS" which is UTC by convention. moment.utc
// parses both as UTC; we render in UTC so the displayed instant matches the
// backend snapshot — "all displayed times UTC".
const SURFACE_TS_FORMAT = 'DD MMM YYYY - HH:mm:ss';

// Display build-time for a surface payload: the shared extractor owns the
// push/reply unwrap and the timestamp-vs-snapshot_time precedence (so the two
// can't drift); this only formats the result as a UTC string. moment.utc parses
// both an ISO-8601 instant and a naive "YYYY-MM-DD HH:MM:SS" as UTC.
const extractSurfaceBuildTimeUtc = (surface: unknown): string | null => {
  const raw = extractAsOf(surface);
  if (raw == null) return null;
  const parsed = moment.utc(raw);
  return parsed.isValid() ? `${parsed.format(SURFACE_TS_FORMAT)} UTC` : null;
};

const formatSpot = (spot: number | null): string =>
  spot == null ? '—' : spot.toLocaleString('en-US', { maximumFractionDigits: 6 });

const fmt = (v: number | null | undefined, digits: number): string =>
  v == null || !Number.isFinite(v) ? '—' : v.toFixed(digits);

// A vol, carried as a fraction, shown as a percentage to 2 dp — the house
// precision for every vol column in the join (ATMF, ETH ATMF, modelled, smile).
const fmtVolPct = (v: number | null | undefined): string =>
  v == null || !Number.isFinite(v) ? '—' : `${(v * 100).toFixed(2)}%`;

// Overlay annualisation. The stdev-vs-EWMA overlay is built from the daily
// close series (see the overlay note), so its per-day stdevs are annualised
// x sqrt(365) for display — a DISPLAY transform only, leaving the dimensionless
// ratios untouched.
const ANNUALISE = Math.sqrt(365);

// Formats an ALREADY-annualised vol (decimal) as a percentage to 1 dp. The
// compute now returns frequency-annualised hvols, so the metrics format them
// directly — no ×√N here.
const fmtAnnualisedVol = (v: number | null | undefined): string =>
  v == null || !Number.isFinite(v) ? '—' : `${(v * 100).toFixed(1)}%`;

// An ALREADY-annualised vol spread (decimal), shown in vol points — the
// additive-mode premium. Signed, since a token can sit below ETH. The metrics
// readout keeps 1 dp (its default); the term-structure table passes digits=2.
const fmtAnnualVolPts = (v: number | null | undefined, digits = 1): string =>
  v == null || !Number.isFinite(v) ? '—' : (v * 100).toFixed(digits);

// A difference already carried in vol POINTS (not a fraction), shown signed.
const fmtDeltaPts = (v: number | null): string =>
  v == null || !Number.isFinite(v) ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(2)}`;

// A published Future — a price, not a vol. Decimals follow the token's price
// magnitude via the shared decimal map (BNB ~790 → 2 dp, a sub-$10 token → 4 dp)
// rather than a flat width, so the column reads at the token's natural scale.
const fmtPrice = (v: number | null, tokenSymbol: string): string =>
  v == null || !Number.isFinite(v) ? '—' : formatTokenPrice(v, tokenSymbol);

// Smile representation for the join's optional smile columns. 'rr-fly' is the
// risk-reversal / butterfly pairs (the default, as the surface widget shows);
// 'cp' is the delta vols. Both are sourced verbatim from the published payload.
type SmileRep = 'rr-fly' | 'cp';

const SMILE_COLUMNS: Record<SmileRep, readonly (keyof PublishedAtm)[]> = {
  'rr-fly': PUBLISHED_RR_FLY_KEYS,
  cp: PUBLISHED_CP_KEYS,
};

// One row of the Modelled-vs-Published join, keyed by expiry. Computed fields
// are null for a published-only expiry; `pub` is null for a computed-only one.
interface JoinedRow {
  expiry: string;
  T: number | null;
  ethAtmf: number | null;
  scale: number | null;
  spread: number | null;
  modelledAtmf: number | null;
  deltaVolPts: number | null;
  pub: PublishedAtm | null;
}

// Full outer join of the computed per-pillar output and the published-surface
// map on the expiry string. Computed order (the term structure) comes first and
// is never re-sorted; published-only expiries are appended in sorted order.
// Δ is modelled − published in vol points, using the MODE-CORRECT modelled ATMF
// (tokenAtmf already tracks the multiplicative/additive toggle).
const buildJoinedRows = (
  computed: AltSurfaceComputation,
  publishedAtm: PublishedAtmByExpiry,
): JoinedRow[] => {
  const seen = new Set<string>();
  const rows: JoinedRow[] = computed.perPillar.map((p) => {
    seen.add(p.expiry);
    const pub = publishedAtm[p.expiry] ?? null;
    const deltaVolPts =
      pub?.ATMF != null && Number.isFinite(p.tokenAtmf) ? (p.tokenAtmf - pub.ATMF) * 100 : null;
    return {
      expiry: p.expiry,
      T: p.T,
      ethAtmf: p.ethAtmf,
      scale: p.scale,
      spread: p.spread,
      modelledAtmf: p.tokenAtmf,
      deltaVolPts,
      pub,
    };
  });
  Object.keys(publishedAtm)
    .filter((expiry) => !seen.has(expiry))
    .sort()
    .forEach((expiry) => {
      rows.push({
        expiry,
        T: null,
        ethAtmf: null,
        scale: null,
        spread: null,
        modelledAtmf: null,
        deltaVolPts: null,
        pub: publishedAtm[expiry],
      });
    });
  return rows;
};

// Builds the three realised-vol lines for one leg (token or ETH): the raw
// rolling stdev and its short/long EWMAs, each annualised for display and
// index-aligned to the UTC daily dates. Colours are theme-resolved design
// tokens — no literals — so they re-resolve on a theme flip.
const buildLegVolSeries = (
  rows: DailyCloseRow[],
  column: string,
  legLabel: string,
  params: AltSurfaceParams,
  mode: ResolvedThemeMode,
): AltChartSeries[] => {
  if (rows.length === 0) return [];
  const dates = rows.map((row) => row.date);
  const { stdShort, stdLong, ewmShort, ewmLong } = altVolSeries(
    columnCloses(rows, column),
    params.windowSize,
    params.hlShort,
    params.windowLong ?? params.windowSize,
    params.hlLong,
  );
  const pack = (
    arr: number[],
    idSuffix: string,
    label: string,
    tokenPath: string,
  ): AltChartSeries => ({
    id: `${legLabel}-${idSuffix}`,
    label,
    color: getTokenValue(tokenPath, mode),
    data: dates.map((date, i) => ({ time: date, value: arr[i] * ANNUALISE })),
  });
  const windowLong = params.windowLong ?? params.windowSize;
  return [
    pack(stdShort, 'std-s', `rolling-${params.windowSize}d stdev (short)`, 'charts.oliveGreen'),
    pack(ewmShort, 'short', `EWMA ${params.hlShort}d`, 'charts.turquoise'),
    pack(stdLong, 'std-l', `rolling-${windowLong}d stdev (long)`, 'charts.yellow'),
    pack(ewmLong, 'long', `EWMA ${params.hlLong}d`, 'charts.purple'),
  ];
};

const MetricRow = ({ label, value }: { label: string; value: string }) => (
  <div className="alt-surface-metrics__row">
    <span className="alt-surface-metrics__label">{label}</span>
    <span className="alt-surface-metrics__value">{value}</span>
  </div>
);

// Subheading separating one group of metric rows from the next, so the readout
// reads as grouped sections rather than a flat wall of rows.
const MetricGroup = ({ label }: { label: string }) => (
  <div className="alt-surface-metrics__group">{label}</div>
);

// Realised-vol overlay: for the token and for ETH, the raw rolling stdev and
// its short/long EWMAs, annualised. Two stacked small-multiples keep each chart
// to three legible lines sharing one colour legend. This is the visual of the
// realised-vol inputs that drive the ratios.
const TimeSeriesPanel = ({
  tokenSeries,
  ethSeries,
  tokenName,
  isHourly,
}: {
  tokenSeries: AltChartSeries[];
  ethSeries: AltChartSeries[];
  tokenName: string;
  isHourly: boolean;
}) => {
  const hasData = tokenSeries.some((s) => s.data.length > 0);
  return (
    <section className="alt-surface-panel alt-surface-panel--wide">
      <h3 className="alt-surface-panel__title">Realised vol — stdev vs EWMA overlay</h3>
      <p className="alt-surface-panel__note">
        realised vol, annualised (x&radic;365) — daily returns, rolling stdev and its short/long
        EWMAs; UTC
        {isHourly &&
          ' · overlay stays on the daily-resampled series; the Hourly frequency drives the Metrics and the Modelled-vs-Published table'}
      </p>
      {hasData ? (
        <div className="alt-surface-charts">
          <div className="alt-surface-charts__block">
            <span className="alt-surface-charts__caption">{tokenName}</span>
            <AltSurfaceChart series={tokenSeries} />
          </div>
          <div className="alt-surface-charts__block">
            <span className="alt-surface-charts__caption">ETH</span>
            <AltSurfaceChart series={ethSeries} />
          </div>
        </div>
      ) : (
        <div className="alt-surface-panel__empty">Plots once the daily closes have arrived.</div>
      )}
    </section>
  );
};

// A segmented radio-group: a horizontal set of mutually exclusive options
// rendered as styled radio buttons. The active option carries an `--active`
// modifier on the shared option class. Block/option class names are passed in so
// one component drives both the `alt-surface-mode` and `alt-surface-seg` looks.
function SegmentedRadio<T extends string>({
  name,
  ariaLabel,
  value,
  options,
  onChange,
  groupClassName,
  optionClassName,
}: {
  name: string;
  ariaLabel: string;
  value: T;
  options: readonly { value: T; text: string }[];
  onChange: (next: T) => void;
  groupClassName: string;
  optionClassName: string;
}) {
  return (
    <div className={groupClassName} role="radiogroup" aria-label={ariaLabel}>
      {options.map((opt) => (
        <label
          key={opt.value}
          className={`${optionClassName}${value === opt.value ? ` ${optionClassName}--active` : ''}`}
        >
          <input
            type="radio"
            name={name}
            value={opt.value}
            checked={value === opt.value}
            onChange={() => onChange(opt.value)}
          />
          {opt.text}
        </label>
      ))}
    </div>
  );
}

// Representation toggle options for the smile columns.
const SMILE_REP_OPTIONS: { value: SmileRep; text: string }[] = [
  { value: 'rr-fly', text: 'RR/FLY' },
  { value: 'cp', text: 'C/P' },
];

// Return-frequency and premium-methodology toggle options.
const FREQUENCY_OPTIONS: { value: AltSurfaceFrequency; text: string }[] = [
  { value: 'daily', text: 'Daily' },
  { value: 'hourly', text: 'Hourly' },
];

const MODE_OPTIONS: { value: AltSurfaceMode; text: string }[] = [
  { value: 'multiplicative', text: 'Multiplicative' },
  { value: 'additive', text: 'Additive' },
];

const SCALE_CURVE_OPTIONS: { value: AltSurfaceScaleCurve; text: string }[] = [
  { value: 'linear', text: 'Linear' },
  { value: 'geometric', text: 'Geometric' },
  { value: 'power', text: 'Power' },
];

// Exponent slider bounds for the 'power' scale curve. 0.5 = sqrt(T); 1 = linear.
const SCALE_EXPONENT_RANGE: { min: number; max: number; step: number } = {
  min: 0.25,
  max: 2,
  step: 0.05,
};

// Modelled-vs-Published join — the generation chain end to end: per-pillar ETH
// ATMF -> scale/spread(T) -> token ATMF beside the published ATMF and the Δ,
// optionally with the published smile (RR/FLY or C/P). Reads straight off the
// compute output and the published payload — no maths is re-done here. The
// realised-vol derivation lives in the Metrics panel, not duplicated here.
const ComputedAtmPanel = ({
  computed,
  publishedAtm,
  publishedBuildTime,
  tokenName,
  showSmile,
  onToggleSmile,
  smileRep,
  onChangeSmileRep,
}: {
  computed: AltSurfaceComputation | null;
  publishedAtm: PublishedAtmByExpiry;
  publishedBuildTime: string | null;
  tokenName: string;
  showSmile: boolean;
  onToggleSmile: (next: boolean) => void;
  smileRep: SmileRep;
  onChangeSmileRep: (next: SmileRep) => void;
}) => {
  const additive = computed?.mode === 'additive';
  const rows = computed ? buildJoinedRows(computed, publishedAtm) : [];
  const smileKeys = showSmile ? SMILE_COLUMNS[smileRep] : [];
  return (
    <section className="alt-surface-panel alt-surface-panel--combined">
      <div className="alt-surface-panel__titlebar">
        <div className="alt-surface-panel__titlegroup">
          <h3 className="alt-surface-panel__title">Modelled vs Published</h3>
          <p className="alt-surface-panel__subtitle">Published: {publishedBuildTime ?? '—'}</p>
        </div>
        <div className="alt-surface-smile-controls">
          <label className="alt-surface-panel__toggle">
            <input
              type="checkbox"
              checked={showSmile}
              onChange={(e) => onToggleSmile(e.target.checked)}
            />
            Show smile
          </label>
          {showSmile && (
            <SegmentedRadio
              name="alt-surface-smile-rep"
              ariaLabel="Smile representation"
              value={smileRep}
              options={SMILE_REP_OPTIONS}
              onChange={onChangeSmileRep}
              groupClassName="alt-surface-seg"
              optionClassName="alt-surface-seg__option"
            />
          )}
        </div>
      </div>
      {computed && computed.perPillar.length > 0 ? (
        <div className="alt-surface-table-wrap">
          <table className="alt-surface-table">
            <thead>
              <tr>
                <th>Expiry</th>
                <th>Expiry in</th>
                <th>Future</th>
                <th className="alt-surface-cell--emphasis">ATMF (pub)</th>
                <th>ETH ATMF</th>
                <th>{additive ? '+ spread(T)' : '× scale(T)'}</th>
                <th className="alt-surface-cell--emphasis">Modelled ATMF</th>
                <th>Δ (mod − pub)</th>
                {smileKeys.map((key) => (
                  <th key={key}>{key}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.expiry}>
                  <td>{r.expiry}</td>
                  <td>{formatExpiryIn(r.T)}</td>
                  <td>{fmtPrice(r.pub?.Future ?? null, tokenName)}</td>
                  <td className="alt-surface-cell--emphasis">{fmtVolPct(r.pub?.ATMF ?? null)}</td>
                  <td>{fmtVolPct(r.ethAtmf)}</td>
                  <td>
                    {r.modelledAtmf == null
                      ? '—'
                      : additive
                        ? fmtAnnualVolPts(r.spread, 2)
                        : fmt(r.scale, 4)}
                  </td>
                  <td className="alt-surface-cell--emphasis">{fmtVolPct(r.modelledAtmf)}</td>
                  <td>{fmtDeltaPts(r.deltaVolPts)}</td>
                  {smileKeys.map((key) => (
                    <td key={key}>{fmtVolPct(r.pub?.[key] ?? null)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="alt-surface-panel__empty">
          <div className="alt-surface-panel__loading" role="status">
            <CircularProgress size={24} aria-label="Loading data" />
            <span className="alt-surface-panel__loading-label">Loading data…</span>
            <span className="alt-surface-panel__loading-note">
              Computes once the daily closes and the ETH reference surface have arrived.
            </span>
          </div>
        </div>
      )}
    </section>
  );
};

const AltSurfaceBuilder = () => {
  const { tokens, isLoading: registryLoading, error: registryError } = useAvailableTokens();

  const generated = useMemo(() => classifyRegistryTokens(tokens).generated, [tokens]);

  // Default selection: the first Generated token — the sandbox is about
  // generated-token surfaces. Selection is a base_quote string (the wire token).
  const [selected, setSelected] = useState<string>('');
  useEffect(() => {
    if (!selected && generated.length > 0) {
      setSelected(generated[0].baseQuote);
    }
  }, [selected, generated]);

  const selectedToken = useMemo(
    () => tokens.find((t) => t.baseQuote === selected) ?? null,
    [tokens, selected],
  );

  // Memoise the hook input so its identity only changes when the token does.
  // The hook is robust to unmemoised callers (it depends on primitives), but a
  // stable object here is cheap belt-and-braces.
  const altToken = useMemo(
    () => (selectedToken ? altSurfaceTokenFromRegistry(selectedToken) : null),
    [selectedToken],
  );
  const inputs = useAltSurfaceInputs(altToken);

  // Live parameters. Editing recomputes from the cached inputs — no refetch.
  // The slider onChange routes through applyAltSurfaceParam, which integer-
  // clamps to each range and enforces hlShort < hlLong (nudging the other side).
  const [params, setParams] = useState<AltSurfaceParams>(ALT_SURFACE_DEFAULT_PARAMS);
  const updateParam = (key: keyof AltSurfaceParams, raw: string) => {
    const value = Number(raw);
    if (!Number.isFinite(value)) return;
    setParams((prev) => applyAltSurfaceParam(prev, key, value));
  };
  const resetParams = () => setParams(ALT_SURFACE_DEFAULT_PARAMS);

  // Premium-assumption toggle. Multiplicative scales ETH ATMF by scale(T); additive
  // adds an annualised spread(T). Distinct from the theme `resolvedMode`
  // below — this is the methodology mode and drives the whole computed surface.
  const [methodologyMode, setMethodologyMode] = useState<AltSurfaceMode>(ALT_SURFACE_DEFAULT_MODE);

  // Return frequency the realised-vol pipeline runs on. Hourly (default) runs on the raw
  // hourly series with the day-denominated params converted to hourly periods internally;
  // Daily resamples the hourly closes to daily first. Recomputes live on change.
  const [frequency, setFrequency] = useState<AltSurfaceFrequency>(ALT_SURFACE_DEFAULT_FREQUENCY);

  // Shape of the multiplicative ATM scale between front (short_ratio) and back
  // (long_ratio). Linear is the straight-line interpolation; Geometric is a log-linear
  // decay that pulls the belly/back toward long_ratio faster than the line.
  // Multiplicative-mode only; recomputes live.
  const [scaleCurve, setScaleCurve] = useState<AltSurfaceScaleCurve>(
    ALT_SURFACE_DEFAULT_SCALE_CURVE,
  );

  // Exponent for the 'power' scale curve (0.5 = sqrt(T), 1 = linear). Only used
  // when scaleCurve is 'power'; recomputes live.
  const [scaleExponent, setScaleExponent] = useState<number>(ALT_SURFACE_DEFAULT_SCALE_EXPONENT);

  // Smile columns on the combined panel: shown by default as RR/FLY (matching the
  // surface widget's default), hidden to narrow the table to the ATM columns, and
  // switchable to the C/P delta-vol representation. Both reps come from the
  // published payload — toggling re-reads, never recomputes.
  const [showSmile, setShowSmile] = useState(true);
  const [smileRep, setSmileRep] = useState<SmileRep>('rr-fly');

  const computed = useMemo(
    () =>
      computeAltSurface({
        dailyCloses: inputs.dailyCloses,
        hourlyCloses: inputs.hourlyCloses,
        params,
        ethSurface: inputs.ethSurface,
        btcSurface: inputs.btcSurface,
        publishedTokenSurface: inputs.publishedTokenSurface,
        tokenSpot: inputs.tokenSpot,
        mode: methodologyMode,
        frequency,
        scaleCurve,
        scaleExponent,
      }),
    [
      inputs.dailyCloses,
      inputs.hourlyCloses,
      inputs.ethSurface,
      inputs.btcSurface,
      inputs.publishedTokenSurface,
      inputs.tokenSpot,
      params,
      methodologyMode,
      frequency,
      scaleCurve,
      scaleExponent,
    ],
  );

  // Published-surface ATM + wings, keyed by expiry, for the Modelled-vs-Published
  // join. Pulled from the same cached payload the top widget renders — no
  // refetch; recomputed only when the published surface payload changes.
  const publishedAtm = useMemo(
    () => extractPublishedAtm(inputs.publishedTokenSurface),
    [inputs.publishedTokenSurface],
  );

  // Build timestamp of the published surface the join compares against — shown
  // as the combined panel's subtitle and in the Metrics panel. UTC, same cached
  // payload; "—" when the snapshot carries no parseable timestamp.
  const publishedBuildTime = useMemo(
    () => extractSurfaceBuildTimeUtc(inputs.publishedTokenSurface),
    [inputs.publishedTokenSurface],
  );

  const isAdditive = computed?.mode === 'additive';
  const isHourly = frequency === 'hourly';

  // Front → back endpoints of the active premium across the pillar grid:
  // multiplicative scale (dimensionless) or additive spread (annualised vol pts).
  const atmPremiumLabel = useMemo(() => {
    if (!computed || computed.perPillar.length === 0) return '—';
    const first = computed.perPillar[0];
    const last = computed.perPillar[computed.perPillar.length - 1];
    return isAdditive
      ? `${fmtAnnualVolPts(first.spread)} → ${fmtAnnualVolPts(last.spread)} vol pts`
      : `${fmt(first.scale, 4)} → ${fmt(last.scale, 4)}`;
  }, [computed, isAdditive]);

  // Display name for the selected token, shared by the metrics readout and the
  // overlay/walkthrough panels — the wire base_quote, or a neutral placeholder
  // before a token is chosen.
  const tokenName = selected || 'Token';

  // Chart series, re-resolved on parameter and theme change. The vol overlay is
  // built straight from the cached closes (no refetch); the term structure from
  // the compute output.
  const { resolvedMode } = useThemeMode();
  const tokenVolSeries = useMemo(
    () =>
      buildLegVolSeries(
        inputs.dailyCloses,
        DAILY_CLOSE_COLUMNS.TOKEN,
        'token',
        params,
        resolvedMode,
      ),
    [inputs.dailyCloses, params, resolvedMode],
  );
  const ethVolSeries = useMemo(
    () =>
      buildLegVolSeries(inputs.dailyCloses, DAILY_CLOSE_COLUMNS.ETH, 'eth', params, resolvedMode),
    [inputs.dailyCloses, params, resolvedMode],
  );

  const statusLabel = (() => {
    if (registryError) return `Registry error: ${registryError}`;
    if (inputs.error) return `Data error: ${inputs.error}`;
    if (registryLoading || inputs.isLoading) return 'Loading inputs…';
    return `Ready — ${inputs.dailyCloses.length} daily closes${
      inputs.tokenSpot != null ? `, spot ${inputs.tokenSpot}` : ''
    }`;
  })();

  return (
    <div className={`alt-surface-builder alt-surface-builder--${resolvedMode}`}>
      {/* Brand watermark is centred within the Modelled-vs-Published panel and
          each realised-vol graph (CSS ::after, theme-keyed off the root
          alt-surface-builder--${mode} class), not one page-level mark. */}
      <header className="alt-surface-builder__header">
        <h2 className="alt-surface-builder__heading">Alt Surface Builder</h2>
        <span
          className={`alt-surface-builder__status${
            registryError || inputs.error ? ' alt-surface-builder__status--error' : ''
          }`}
        >
          {statusLabel}
        </span>
      </header>

      <div className="alt-surface-builder__token-row">
        <TokenSelector token={selected} setToken={setSelected} fromRegistry />
      </div>

      <div className="alt-surface-builder__grid">
        <ComputedAtmPanel
          computed={computed}
          publishedAtm={publishedAtm}
          publishedBuildTime={publishedBuildTime}
          tokenName={tokenName}
          showSmile={showSmile}
          onToggleSmile={setShowSmile}
          smileRep={smileRep}
          onChangeSmileRep={setSmileRep}
        />

        <section className="alt-surface-panel alt-surface-panel--params">
          <div className="alt-surface-panel__titlebar">
            <h3 className="alt-surface-panel__title">Parameters</h3>
            <button
              type="button"
              className="alt-surface-params__reset"
              onClick={resetParams}
              title="Reset window and half-lives to the methodology defaults"
            >
              Reset to defaults
            </button>
          </div>
          <p className="alt-surface-panel__note">Adjust sliders to change parameters</p>
          <div className="alt-surface-params">
            {PARAM_CONTROLS.map((control) => {
              const range = ALT_SURFACE_PARAM_RANGES[control.key];
              // windowLong is optional on the params type; fall back to its range
              // floor so the slider is always controlled (defaults seed it anyway).
              const value = params[control.key] ?? range.min;
              return (
                <div key={control.key} className="alt-surface-params__row">
                  <div className="alt-surface-params__heading">
                    <span className="alt-surface-params__label">{control.label}</span>
                    <span className="alt-surface-params__value">{`Selected — ${value} d`}</span>
                  </div>
                  <input
                    className="alt-surface-params__slider"
                    type="range"
                    min={range.min}
                    max={range.max}
                    step={range.step}
                    value={value}
                    aria-label={control.label}
                    onChange={(e) => updateParam(control.key, e.target.value)}
                  />
                </div>
              );
            })}
          </div>

          <div className="alt-surface-mode">
            <span className="alt-surface-mode__label">Frequency</span>
            <SegmentedRadio
              name="alt-surface-frequency"
              ariaLabel="Return frequency"
              value={frequency}
              options={FREQUENCY_OPTIONS}
              onChange={setFrequency}
              groupClassName="alt-surface-mode__toggle"
              optionClassName="alt-surface-mode__option"
            />
            <p className="alt-surface-panel__note">
              Hourly: log-returns on the raw hourly closes; window and half-lives are converted to
              hourly periods. Daily: hourly closes resampled to daily, then daily log-returns.
            </p>
          </div>

          <div className="alt-surface-mode">
            <span className="alt-surface-mode__label">Premium methodology</span>
            <SegmentedRadio
              name="alt-surface-mode"
              ariaLabel="Premium methodology"
              value={methodologyMode}
              options={MODE_OPTIONS}
              onChange={setMethodologyMode}
              groupClassName="alt-surface-mode__toggle"
              optionClassName="alt-surface-mode__option"
            />
            <p className="alt-surface-panel__note">
              Multiplicative: token ATMF = ETH ATMF &times; scale(T). Additive: token ATMF = ETH
              ATMF + annualised spread(T).
            </p>
          </div>

          {methodologyMode === 'multiplicative' && (
            <div className="alt-surface-mode">
              <span className="alt-surface-mode__label">Scale curve</span>
              <SegmentedRadio
                name="alt-surface-scale-curve"
                ariaLabel="Scale curve"
                value={scaleCurve}
                options={SCALE_CURVE_OPTIONS}
                onChange={setScaleCurve}
                groupClassName="alt-surface-mode__toggle"
                optionClassName="alt-surface-mode__option"
              />
              <p className="alt-surface-panel__note">
                Linear: straight line from short_ratio to long_ratio in T. Geometric: log-linear
                decay, short &times; (long/short)^u. Power: interpolate in T^p &mdash; p=0.5 is
                sqrt(T), p=1 is linear; lower p decays the belly/back down faster.
              </p>
              {scaleCurve === 'power' && (
                <div className="alt-surface-params__row">
                  <div className="alt-surface-params__heading">
                    <span className="alt-surface-params__label">Power exponent (p)</span>
                    <span className="alt-surface-params__value">{`p — ${scaleExponent.toFixed(2)}${scaleExponent === 0.5 ? ' (sqrt T)' : scaleExponent === 1 ? ' (linear)' : ''}`}</span>
                  </div>
                  <input
                    className="alt-surface-params__slider"
                    type="range"
                    min={SCALE_EXPONENT_RANGE.min}
                    max={SCALE_EXPONENT_RANGE.max}
                    step={SCALE_EXPONENT_RANGE.step}
                    value={scaleExponent}
                    aria-label="Power exponent"
                    onChange={(e) => setScaleExponent(Number(e.target.value))}
                  />
                </div>
              )}
            </div>
          )}
        </section>

        <section className="alt-surface-panel alt-surface-panel--metrics">
          <h3 className="alt-surface-panel__title">Metrics</h3>
          <div className="alt-surface-metrics">
            <MetricGroup label="Realised vol — annualised" />
            <MetricRow
              label={`${tokenName} hvol — short (${params.hlShort}d)`}
              value={fmtAnnualisedVol(computed?.hvolShortToken)}
            />
            <MetricRow
              label={`${tokenName} hvol — long (${params.hlLong}d)`}
              value={fmtAnnualisedVol(computed?.hvolLongToken)}
            />
            <MetricRow
              label={`ETH hvol — short (${params.hlShort}d)`}
              value={fmtAnnualisedVol(computed?.hvolShortEth)}
            />
            <MetricRow
              label={`ETH hvol — long (${params.hlLong}d)`}
              value={fmtAnnualisedVol(computed?.hvolLongEth)}
            />
            <p className="alt-surface-metrics__hint">
              {isHourly
                ? 'hourly returns, annualised x√(365×24); window & half-lives converted to hourly periods (x24); display-only — does not affect the dimensionless ratios'
                : 'daily returns, annualised x√365; display-only — does not affect the dimensionless ratios'}
            </p>

            <MetricGroup label={isAdditive ? 'Spreads & premium' : 'Ratios & scale'} />
            {isAdditive ? (
              <>
                <MetricRow
                  label="Short spread (token − ETH, vol pts)"
                  value={fmtAnnualVolPts(computed?.shortSpread)}
                />
                <MetricRow
                  label="Long spread (token − ETH, vol pts)"
                  value={fmtAnnualVolPts(computed?.longSpread)}
                />
                <MetricRow label="ATM spread (front → back)" value={atmPremiumLabel} />
                <p className="alt-surface-metrics__hint">
                  additive premium (drives the surface): short spread at the nearest pillar, long at
                  the farthest; annualised x&radic;365 to ETH ATMF units
                </p>
              </>
            ) : (
              <>
                <MetricRow label="Short ratio (token / ETH)" value={fmt(computed?.shortRatio, 4)} />
                <MetricRow label="Long ratio (token / ETH)" value={fmt(computed?.longRatio, 4)} />
                <MetricRow label="ATM scale (front → back)" value={atmPremiumLabel} />
                <p className="alt-surface-metrics__hint">
                  multiplicative scale (drives the surface): short ratio at the nearest pillar, long
                  ratio at the farthest
                </p>
              </>
            )}

            <MetricGroup label="Context" />
            <MetricRow
              label="Token / venue"
              value={
                selectedToken
                  ? `${selectedToken.baseQuote} · ${selectedToken.source} · ${selectedToken.exchangeSymbol}`
                  : '—'
              }
            />
            <MetricRow label="Spot rate" value={formatSpot(inputs.tokenSpot)} />
            <MetricRow label="Daily closes" value={String(inputs.dailyCloses.length)} />
            <MetricRow label="Surface build time (UTC)" value={publishedBuildTime ?? '—'} />
          </div>
        </section>

        <TimeSeriesPanel
          tokenSeries={tokenVolSeries}
          ethSeries={ethVolSeries}
          tokenName={tokenName}
          isHourly={isHourly}
        />
      </div>
    </div>
  );
};

export default AltSurfaceBuilder;
